/**
 * Phase Gate side panel — DEC-15 Option 2 (interim).
 *
 * Presence/status from plan `phases[].gate` summary only.
 * Actions via live submission-ID routes (open/submit/approve/reject/
 * approval-state/evidence, and withdrawing a draft). No definition editor, no
 * dead phase-scoped endpoints.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { X, Shield, CheckCircle2, XCircle, HelpCircle, Plus, Paperclip } from 'lucide-react';
import { toast } from 'sonner';

import {
  EVIDENCE_NAME_MAX,
  EVIDENCE_NOTE_MAX,
  EVIDENCE_URL_MAX,
  evidenceContent,
  evidenceLinkError,
} from './evidenceLink';

import { subscribeProjectTasksChanged } from '@/features/projects/phaseTaskChange';
import { ApprovalChainEditor } from './ApprovalChainEditor';
import {
  approvalStepLabel,
  readChainForGate,
  type ApprovalChainStep,
} from './approvalChains.api';
import {
  openGateSubmission,
  submitGateSubmission,
  cancelGateSubmission,
  getApprovalState,
  isAuthForbiddenDenial,
  approveGateSubmission,
  rejectGateSubmission,
  requestMoreInformation,
  isBlankMoreInformationQuestion,
  moreInformationQuestionFromState,
  GATE_MORE_INFORMATION_QUESTION_REQUIRED,
  listGateEvidence,
  attachGateEvidence,
  listGateEvidenceCandidates,
  removeGateEvidence,
  type GateEvidenceCandidate,
  evaluateGateSubmission,
  getGateReadiness,
  readinessBlocksSubmit,
  type GateSubmissionStatus,
  type ChainExecutionState,
  type GateSubmissionEvidence,
  type StepApprovalState,
  type GateEvaluationResult,
  type GateReadiness,
} from './phaseGates.api';
import { GateReadinessSection } from './GateReadinessSection';

import { resolveCallerApprovalAffordance } from '@/features/phase-gates/gateCallerApprovalCopy';
import { UnavailablePanel } from '@/components/ui/feedback/Unavailable';
import { useProjectCapabilities } from '@/features/projects/capabilities';
import type { WorkPlanPhaseGate, WorkPlanTask } from '@/features/work-management/workTasks.api';
import {
  listProjectArtifacts,
  createProjectArtifact,
  createArtifactItem,
} from '@/api/project-artifacts.api';
import { formatGateSubmitRefusal } from './gateSubmitRefusal';
import { awaitingApproversText } from './awaitingApproversText';
import { describeGateState } from '@/features/projects/components/ProjectOverviewPhaseGateStrip';
import { SelfApprovedBadge } from '@/features/governance/selfApprovalDisplay';

export interface PhaseGatePanelProps {
  projectId: string;
  phaseId: string;
  phaseName: string;
  /** Next phase by sort order, when the plan already has one. */
  nextPhaseName?: string | null;
  /** Plan-embedded gate summary — source of presence/status (DEC-15). */
  gate: WorkPlanPhaseGate | null;
  /** Tasks in this phase — used to name which ones are still open. */
  phaseTasks?: WorkPlanTask[];
  /** Optional deep-link submission id from query params. */
  initialSubmissionId?: string | null;
  /**
   * WITHDRAW-ELIGIBLE-1: the server said this caller may withdraw this gate's draft now
   * (`canWithdraw === true` on the plan gate; the cancel route enforces the same rule).
   * The server still refuses a stale click and its message is shown as it comes.
   * Absent means no: the control fails closed.
   */
  canWithdrawDraft?: boolean;
  onClose: () => void;
  /** Refresh plan after mutations so strip/status stay honest. */
  onChanged?: () => void;
}

const STATUS_STYLES: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-800',
  SUBMITTED: 'bg-blue-100 text-blue-800',
  APPROVED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-gray-100 text-gray-500',
  // G-R8 — non-punitive; must not reuse REJECTED red.
  MORE_INFORMATION: 'bg-amber-100 text-amber-900',
};

/** The artifact "Add evidence" writes into; found by this name and the custom type, else created. */
const EVIDENCE_ARTIFACT_NAME = 'Gate evidence';
/** Items per picker page, requested from the server's evidence-candidates route. */
const PICKER_PAGE_SIZE = 20;

/**
 * An attached item's link and note, as the evidence read returns them (EVIDENCE-READ-CONTENT-1).
 * The server re-validates on the way out; the link is still checked here before it becomes an
 * anchor, because a client must never trust a URL it is about to render.
 */
function EvidenceDetails({ content }: { content: unknown }): JSX.Element | null {
  const { url, note } = evidenceContent(content);
  if (!url && !note) return null;
  return (
    <span className="block text-xs text-gray-500">
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-indigo-700 underline"
          title={url}
          data-testid="phase-gate-evidence-link"
        >
          Open link
        </a>
      ) : null}
      {url && note ? ' · ' : null}
      {note ? <span data-testid="phase-gate-evidence-note">{note}</span> : null}
    </span>
  );
}

export function PhaseGatePanel({
  projectId,
  phaseId,
  phaseName,
  nextPhaseName = null,
  gate,
  phaseTasks = [],
  initialSubmissionId = null,
  canWithdrawDraft = false,
  onClose,
  onChanged,
}: PhaseGatePanelProps) {
  const { use_gates: useGates } = useProjectCapabilities();

  const [loading, setLoading] = useState(true);
  /** After first hydrate, plan refresh must not blank the panel (clears "Approvers saved."). */
  const hasHydratedRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [status, setStatus] = useState<GateSubmissionStatus | null>(null);
  const [chainSteps, setChainSteps] = useState<ApprovalChainStep[] | null>(null);
  const [chainReadReady, setChainReadReady] = useState(false);
  /** Survives editor remount; cleared when the gate definition changes. */
  const [approversSaved, setApproversSaved] = useState(false);
  const [chainExecutionState, setChainExecutionState] =
    useState<ChainExecutionState | null>(null);
  /**
   * PHASEGATEPANEL-REJECTION-1: approval-state read outcome.
   * - `ready` — payload present (may still be empty steps)
   * - `empty` — genuine null from a successful GET (no state yet)
   * - `denied` — HTTP 403 AUTH_FORBIDDEN (#1090 wire). Never collapse into empty.
   */
  const [approvalStateRead, setApprovalStateRead] = useState<
    'ready' | 'empty' | 'denied' | null
  >(null);
  const [evidence, setEvidence] = useState<GateSubmissionEvidence[]>([]);
  /** The attached-evidence read failed: the list says Unavailable, it does not read as "none". */
  const [evidenceFailed, setEvidenceFailed] = useState(false);
  const [evaluation, setEvaluation] = useState<GateEvaluationResult | null>(null);
  const [readiness, setReadiness] = useState<GateReadiness | null>(null);
  const [attachables, setAttachables] = useState<GateEvidenceCandidate[]>([]);
  /** The server's count of every item in the project (the page above is only a slice of it). */
  const [attachablesTotal, setAttachablesTotal] = useState(0);
  const pickerPageRef = useRef(0);
  /** The project's items could not be read: the picker says so, it does not read as "none". */
  const [attachablesFailed, setAttachablesFailed] = useState(false);
  /** The server refused this caller (403) on the shared attach guard: no picker, no Add evidence. */
  const [attachDenied, setAttachDenied] = useState(false);
  const [pickerPage, setPickerPage] = useState(0);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState('');
  const [addUrl, setAddUrl] = useState('');
  const [addNote, setAddNote] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [decisionNote, setDecisionNote] = useState('');
  const [infoQuestion, setInfoQuestion] = useState('');
  const [infoQuestionError, setInfoQuestionError] = useState<string | null>(null);
  const [infoResponse, setInfoResponse] = useState('');
  const [blockMessage, setBlockMessage] = useState<string | null>(null);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);
  const [showOpenTasks, setShowOpenTasks] = useState(false);

  const definitionExists = gate?.definitionExists === true;
  const described = describeGateState(gate, {
    gatePhaseName: phaseName,
    blockedPhaseName: nextPhaseName,
  });

  /**
   * G5-WIRE-1: the chain is frozen exactly while a submission is DRAFT or
   * SUBMITTED — the two statuses `assertNoOpenSubmission` queries on the
   * backend. Deriving it here, from the plan summary the panel already holds,
   * is what lets the lock be shown BEFORE the admin fills in a form that cannot
   * save. Discovering it from GATE_CHAIN_LOCKED would mean discovering it after.
   */
  const approversLocked =
    gate?.submissionStatus === 'DRAFT' || gate?.submissionStatus === 'SUBMITTED';

  const refreshSubmissionSurface = useCallback(
    async (sid: string) => {
      // ⚠️ Do NOT `.catch(() => null)` on getApprovalState — that turns a real
      // AUTH_FORBIDDEN 403 (#1090) back into "no state" and undoes the envelope
      // fix at the last step (PHASEGATEPANEL-REJECTION-1 / #1017 FE).
      const [stateResult, ev, evalResult, readinessResult] = await Promise.all([
        getApprovalState(sid).then(
          (state) =>
            ({ kind: 'ok', state }) as const,
          (err: unknown) =>
            isAuthForbiddenDenial(err)
              ? ({ kind: 'denied' } as const)
              : ({ kind: 'error', err } as const),
        ),
        // A failed read is not "no evidence": null marks it so the list says Unavailable.
        listGateEvidence(sid).catch(() => null),
        evaluateGateSubmission(sid).catch(() => null),
        typeof getGateReadiness === 'function'
          ? getGateReadiness(sid)
              .then((body) => (body && Array.isArray(body.items) ? body : null))
              .catch(() => null)
          : Promise.resolve(null),
      ]);

      if (stateResult.kind === 'denied') {
        setApprovalStateRead('denied');
        setChainExecutionState(null);
      } else if (stateResult.kind === 'ok') {
        setChainExecutionState(stateResult.state);
        setApprovalStateRead(stateResult.state == null ? 'empty' : 'ready');
      } else {
        // Non-auth failure: leave prior surface; surface via toast below.
        setApprovalStateRead(null);
        setChainExecutionState(null);
        const msg =
          stateResult.err instanceof Error
            ? stateResult.err.message
            : typeof stateResult.err === 'object' &&
                stateResult.err &&
                'message' in stateResult.err &&
                typeof (stateResult.err as { message: unknown }).message ===
                  'string'
              ? (stateResult.err as { message: string }).message
              : 'Failed to load approval state';
        toast.error(msg);
      }

      setEvidence(ev ?? []);
      setEvidenceFailed(ev === null);
      setEvaluation(evalResult);
      setReadiness(readinessResult);
      // Prefer plan status; approval-state does not always echo submission status.
    },
    [],
  );

  /**
   * GATE-EVIDENCE-ATTACH-1: one page of the server's evidence-candidates (every undeleted
   * item in the project, with a total). `page` is 0-based here; the server's is 1-based.
   */
  const loadAttachables = useCallback(
    async (sid: string | null, page: number = pickerPageRef.current): Promise<void> => {
      if (!sid) return;
      try {
        const res = await listGateEvidenceCandidates(sid, {
          page: page + 1,
          limit: PICKER_PAGE_SIZE,
        });
        pickerPageRef.current = page;
        setPickerPage(page);
        setAttachables(res.items);
        setAttachablesTotal(res.total);
        setAttachablesFailed(false);
        setAttachDenied(false);
      } catch (err: unknown) {
        setAttachables([]);
        setAttachablesTotal(0);
        // The candidates read and the attach route share one guard (project manager, workspace
        // owner or admin). A 403 here is the server saying this caller cannot attach: it is not
        // a failed read, and nothing is offered or written. Any other error stays Unavailable.
        const status = (err as { response?: { status?: number } } | null)?.response?.status;
        const denied = status === 403;
        setAttachDenied(denied);
        setAttachablesFailed(!denied);
      }
    },
    [],
  );

  const loadData = useCallback(async () => {
    // Soft refresh: after the first paint, do not flip `loading` — that unmounts
    // ApprovalChainEditor and clears "Approvers saved." (APPROVER-EDITOR-1).
    if (!hasHydratedRef.current) {
      setLoading(true);
    }
    setBlockMessage(null);
    try {
      if (!definitionExists) {
        setSubmissionId(null);
        setStatus(null);
        setChainExecutionState(null);
        setApprovalStateRead(null);
        setEvidence([]);
        setEvaluation(null);
        setReadiness(null);
        return;
      }

      const fromPlan = gate?.submissionId?.trim() || null;
      const fromQuery = initialSubmissionId?.trim() || null;
      // A withdrawn (CANCELLED) request is over: the plan still reports it as the gate's latest,
      // but there is no open request, so the panel offers a new one rather than showing nothing.
      const latestCanceled = gate?.submissionStatus === 'CANCELLED';
      const sid = latestCanceled ? null : fromQuery || fromPlan;
      const planStatus = (latestCanceled
        ? null
        : (gate?.submissionStatus ?? null)) as GateSubmissionStatus | null;
      setStatus(planStatus);
      setSubmissionId(sid);

      if (sid) {
        await refreshSubmissionSurface(sid);
      } else {
        setChainExecutionState(null);
        setApprovalStateRead(null);
        setEvidence([]);
        setEvaluation(null);
        setReadiness(null);
      }

      if (
        !sid ||
        planStatus === 'DRAFT' ||
        planStatus === 'REJECTED' ||
        planStatus === 'MORE_INFORMATION' ||
        planStatus == null
      ) {
        await loadAttachables(sid);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load gate data';
      toast.error(msg);
    } finally {
      setLoading(false);
      hasHydratedRef.current = true;
    }
  }, [
    definitionExists,
    gate?.submissionId,
    gate?.submissionStatus,
    initialSubmissionId,
    loadAttachables,
    refreshSubmissionSurface,
  ]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    return subscribeProjectTasksChanged((changedProjectId) => {
      if (changedProjectId !== projectId || !submissionId) return;
      void refreshSubmissionSurface(submissionId);
    });
  }, [projectId, submissionId, refreshSubmissionSurface]);

  /**
   * APPROVER-EDITOR-1: hydrate the authoring form from the live chain GET.
   * Without this, the editor always starts at empty ROLE/ADMIN even when the
   * backend already stores requiredUserId.
   */
  useEffect(() => {
    const gateDefinitionId = gate?.gateDefinitionId?.trim() || null;
    if (!definitionExists || !gateDefinitionId || approversLocked) {
      setChainSteps(null);
      setChainReadReady(true);
      return;
    }

    let cancelled = false;
    setChainReadReady(false);
    void readChainForGate(gateDefinitionId)
      .then((result) => {
        if (cancelled) return;
        if (result.outcome === 'CHAIN') {
          setChainSteps(result.chain.steps);
        } else {
          setChainSteps([]);
        }
        setChainReadReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        setChainSteps([]);
        setChainReadReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [definitionExists, gate?.gateDefinitionId, approversLocked]);

  useEffect(() => {
    setApproversSaved(false);
  }, [gate?.gateDefinitionId]);

  const callerApproval = resolveCallerApprovalAffordance(chainExecutionState);
  const approveDisabled = !callerApproval.callerCanApprove;
  const approveDisabledReason = callerApproval.reasonCopy;

  const handleResubmit = async () => {
    if (status === 'MORE_INFORMATION' && infoResponse.trim() === '') return;
    setBusy(true);
    setBlockMessage(null);
    try {
      const opened = await openGateSubmission({ projectId, phaseId });
      const submitted = await submitGateSubmission(
        opened.id,
        status === 'MORE_INFORMATION' ? { response: infoResponse } : undefined,
      );
      setSubmissionId(submitted.id);
      setStatus((submitted.status as GateSubmissionStatus) || 'SUBMITTED');
      setInfoResponse('');
      await refreshSubmissionSurface(submitted.id);
      toast.success('Submitted for approval');
      onChanged?.();
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      const errObj = err as { response?: { data?: { message?: string } }; message?: string };
      toast.error(
        code === 'MORE_INFORMATION_RESPONSE_REQUIRED'
          ? 'A response is required'
          : errObj?.response?.data?.message || errObj?.message || 'Resubmit failed',
      );
    } finally {
      setBusy(false);
    }
  };

  const handleOpenSubmission = async () => {
    setBusy(true);
    setBlockMessage(null);
    try {
      const sub = await openGateSubmission({ projectId, phaseId });
      setSubmissionId(sub.id);
      setStatus((sub.status as GateSubmissionStatus) || 'DRAFT');
      await refreshSubmissionSurface(sub.id);
      await loadAttachables(sub.id);
      toast.success(
        sub.status === 'DRAFT' ? 'Draft submission ready' : 'Submission opened',
      );
      onChanged?.();
    } catch (err: unknown) {
      const errObj = err as { response?: { data?: { message?: string } }; message?: string };
      toast.error(
        errObj?.response?.data?.message ||
          errObj?.message ||
          'Failed to open gate submission',
      );
    } finally {
      setBusy(false);
    }
  };

  const handleAttachEvidence = async () => {
    if (!submissionId || selectedItemIds.size === 0) return;
    setBusy(true);
    try {
      for (const artifactItemId of selectedItemIds) {
        await attachGateEvidence(submissionId, artifactItemId);
      }
      setSelectedItemIds(new Set());
      await refreshSubmissionSurface(submissionId);
      await loadAttachables(submissionId);
      toast.success('Evidence attached');
      onChanged?.();
    } catch (err: unknown) {
      const errObj = err as { response?: { data?: { message?: string; code?: string } } };
      toast.error(errObj?.response?.data?.message || 'Failed to attach evidence');
    } finally {
      setBusy(false);
    }
  };

  /**
   * GATE-EVIDENCE-ATTACH-1: create an item (name + link + optional note) in the project's
   * "Gate evidence" artifact and attach it, using the existing create-artifact, create-item
   * and attach routes. Three calls, no shared transaction: if the attach is refused after
   * the item was saved, say so and show the server's message; the item then appears in the
   * list below to attach later.
   */
  const handleAddEvidence = async (): Promise<void> => {
    if (!submissionId || attachDenied) return;
    const name = addName.trim();
    const note = addNote.trim();
    if (!name) return setAddError('Enter a name.');
    if (name.length > EVIDENCE_NAME_MAX) return setAddError(`The name is longer than ${EVIDENCE_NAME_MAX} characters.`);
    const linkProblem = evidenceLinkError(addUrl);
    if (linkProblem) return setAddError(linkProblem);
    if (note.length > EVIDENCE_NOTE_MAX) return setAddError(`The note is longer than ${EVIDENCE_NOTE_MAX} characters.`);
    setBusy(true);
    setAddError(null);
    let savedItemId: string | null = null;
    try {
      const artifacts = await listProjectArtifacts(projectId);
      const holder =
        artifacts.find((a) => a.type === 'custom' && a.name === EVIDENCE_ARTIFACT_NAME) ??
        (await createProjectArtifact(projectId, { type: 'custom', name: EVIDENCE_ARTIFACT_NAME }));
      const item = await createArtifactItem(projectId, holder.id, {
        name,
        content: { url: addUrl.trim(), ...(note ? { note } : {}) },
      });
      savedItemId = item.id;
      await attachGateEvidence(submissionId, item.id);
      setAddName('');
      setAddUrl('');
      setAddNote('');
      setAddOpen(false);
      await loadAttachables(submissionId);
      await refreshSubmissionSurface(submissionId);
      toast.success('Evidence added');
      onChanged?.();
    } catch (err: unknown) {
      const errObj = err as { response?: { data?: { message?: string } }; message?: string };
      const msg = errObj?.response?.data?.message || errObj?.message || 'Could not add evidence.';
      setAddError(
        savedItemId
          ? `The item was saved but could not be attached: ${msg} You can attach it from the list.`
          : msg,
      );
      if (savedItemId) await loadAttachables(submissionId);
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveEvidence = async (evidenceId: string) => {
    if (!submissionId) return;
    setBusy(true);
    try {
      await removeGateEvidence(submissionId, evidenceId);
      await refreshSubmissionSurface(submissionId);
      onChanged?.();
    } catch (err: unknown) {
      const errObj = err as { response?: { data?: { message?: string } } };
      toast.error(errObj?.response?.data?.message || 'Failed to remove evidence');
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async () => {
    if (!submissionId) return;
    setBusy(true);
    setBlockMessage(null);
    try {
      const sub = await submitGateSubmission(submissionId);
      setStatus((sub.status as GateSubmissionStatus) || 'SUBMITTED');
      await refreshSubmissionSurface(submissionId);
      toast.success('Gate submitted for approval');
      onChanged?.();
    } catch (err: unknown) {
      const msg = formatGateSubmitRefusal(err);
      setBlockMessage(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Withdraw a DRAFT (GATE-DRAFT-WITHDRAW-1). Applied only after the server accepts;
   * a refusal (403) or a state change (409) shows the API's own message.
   */
  const handleWithdraw = async (): Promise<void> => {
    if (!submissionId) return;
    setBusy(true);
    setWithdrawError(null);
    try {
      await cancelGateSubmission(submissionId);
      setSubmissionId(null);
      setStatus(null);
      setEvidence([]);
      toast.success('Draft withdrawn');
      onChanged?.();
    } catch (err: unknown) {
      const errObj = err as {
        response?: { status?: number; data?: { message?: string } };
        message?: string;
      };
      setWithdrawError(
        errObj?.response?.data?.message || errObj?.message || 'Could not withdraw this draft.',
      );
      // 409: the request changed under us; reload the plan so the panel shows the current state.
      if (errObj?.response?.status === 409) onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  const handleApprove = async () => {
    if (!submissionId) return;
    if (!callerApproval.callerCanApprove) {
      toast.error(callerApproval.reasonCopy || 'You cannot approve this gate.');
      return;
    }
    setBusy(true);
    try {
      const state = await approveGateSubmission(
        submissionId,
        decisionNote || undefined,
      );
      setChainExecutionState(state);
      setDecisionNote('');
      if (state.chainStatus === 'COMPLETED') {
        setStatus('APPROVED');
      }
      toast.success('Gate approved');
      onChanged?.();
      await refreshSubmissionSurface(submissionId);
    } catch (err: unknown) {
      const errObj = err as { response?: { data?: { message?: string } } };
      toast.error(errObj?.response?.data?.message || 'Approve failed');
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    if (!submissionId) return;
    if (decisionNote.trim().length === 0) return;
    setBusy(true);
    try {
      const state = await rejectGateSubmission(
        submissionId,
        decisionNote.trim(),
      );
      setChainExecutionState(state);
      setDecisionNote('');
      setStatus('REJECTED');
      toast.success('Gate rejected');
      onChanged?.();
      await refreshSubmissionSurface(submissionId);
    } catch (err: unknown) {
      const errObj = err as { response?: { data?: { message?: string } } };
      toast.error(errObj?.response?.data?.message || 'Reject failed');
    } finally {
      setBusy(false);
    }
  };

  const handleAskInformation = async () => {
    if (!submissionId) return;
    if (isBlankMoreInformationQuestion(infoQuestion)) {
      setInfoQuestionError(GATE_MORE_INFORMATION_QUESTION_REQUIRED);
      return;
    }
    setBusy(true);
    setInfoQuestionError(null);
    try {
      const state = await requestMoreInformation(submissionId, infoQuestion);
      setChainExecutionState(state);
      setInfoQuestion('');
      setDecisionNote('');
      setStatus('MORE_INFORMATION');
      toast.success('Requested changes');
      onChanged?.();
      await refreshSubmissionSurface(submissionId);
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      if (code === GATE_MORE_INFORMATION_QUESTION_REQUIRED) {
        setInfoQuestionError(GATE_MORE_INFORMATION_QUESTION_REQUIRED);
        toast.error(GATE_MORE_INFORMATION_QUESTION_REQUIRED);
      } else {
        const errObj = err as { response?: { data?: { message?: string } }; message?: string };
        toast.error(
          errObj?.response?.data?.message ||
            errObj?.message ||
            'Request failed',
        );
      }
    } finally {
      setBusy(false);
    }
  };

  if (!useGates) {
    return null;
  }

  if (loading) {
    return (
      <div
        className="fixed top-14 bottom-0 right-0 z-[60] flex w-[460px] items-center justify-center border-l border-gray-200 bg-white shadow-xl"
        data-testid="phase-gate-panel-loading"
      >
        <span className="text-gray-500">Loading gate data...</span>
      </div>
    );
  }

  const showDraftActions =
    definitionExists &&
    !!submissionId &&
    (status === 'DRAFT' || status == null);
  const showReopen =
    definitionExists &&
    (status === 'REJECTED' || status === 'MORE_INFORMATION');
  const showOpenCta =
    definitionExists &&
    !submissionId &&
    status !== 'APPROVED' &&
    status !== 'SUBMITTED' &&
    status !== 'REJECTED' &&
    status !== 'MORE_INFORMATION';
  const infoQuestionFromState = moreInformationQuestionFromState(chainExecutionState);
  const infoQuestionBlank = isBlankMoreInformationQuestion(infoQuestion);
  /**
   * D6-REVIEW-REACH-1: the decision section follows the SERVER's answer.
   *
   * This was `isAdmin`, which is the wrong axis. An approver is an ASSIGNMENT,
   * not a role (founder ruling), and the backend admits a MEMBER-seat user
   * named in `required_user_id` — the eligibility leaf tests that field FIRST
   * and returns on it, before any role comparison, and carries no admin
   * wildcard since G7. Gating the UI on a platform role therefore hid the
   * controls from exactly the people the server had already authorised.
   *
   * `callerCanApprove` comes from /approval-state, which computes it from the
   * same leaf the approve transaction enforces, in the same order. Binding
   * here means the panel can never advertise a decision the server will refuse
   * — and, just as importantly, can never withhold one it would allow.
   *
   * ⚠️ `resolveCallerApprovalAffordance` returns callerCanApprove:false when
   * the field is ABSENT, so a server that did not answer is treated as a
   * refusal rather than silently falling back to a client-side role test.
   */
  const showApprove =
    definitionExists &&
    status === 'SUBMITTED' &&
    callerApproval.callerCanApprove &&
    !!submissionId;

  // GATE-EVIDENCE-ATTACH-1: the server pages the project's items with a total, and names
  // attached evidence, so the panel never prints an id.
  const pickerPages = Math.max(1, Math.ceil(attachablesTotal / PICKER_PAGE_SIZE));
  const pickerFrom = attachables.length === 0 ? 0 : pickerPage * PICKER_PAGE_SIZE + 1;
  const pickerTo = pickerPage * PICKER_PAGE_SIZE + attachables.length;

  return (
    <div
      className="fixed top-14 bottom-0 right-0 z-[60] flex w-[460px] flex-col overflow-hidden border-l border-gray-200 bg-white shadow-xl"
      data-testid="phase-gate-panel"
      data-definition-exists={definitionExists ? 'true' : 'false'}
      data-submission-id={submissionId ?? ''}
      data-submission-status={status ?? ''}
    >
      <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 p-4">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-indigo-600" />
          <h2 className="text-lg font-semibold text-gray-900">Gate: {phaseName}</h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 hover:bg-gray-200"
          aria-label="Close gate panel" title="Close gate panel"
        >
          <X className="h-5 w-5 text-gray-500" />
        </button>
      </div>

      <div className="flex-1 space-y-6 overflow-y-auto p-4">
        {status === 'SUBMITTED' ? (
          <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950" data-testid="phase-gate-frozen">
            Phase &quot;{phaseName}&quot; is frozen while its gate is under review. It reopens when the gate is approved, rejected or sent back for more information. Evidence on a submitted gate attempt is part of the record and cannot be removed.
          </p>
        ) : null}
        {/* Status from plan summary */}
        <section data-testid="phase-gate-status">
          <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-700">
            Status
          </h3>
          {!definitionExists ? (
            <p className="text-sm text-gray-500" data-testid="phase-gate-no-definition">
              No gate is configured for this phase. Gate definitions are installed
              with the project template — they cannot be edited here.
            </p>
          ) : (
            <div className="space-y-2 rounded-lg bg-gray-50 p-3 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-gray-900">{described.title}</span>
                {status ? (
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[status] || ''}`}
                    data-testid="phase-gate-status-badge"
                  >
                    {status}
                  </span>
                ) : null}
              </div>
              {described.detail ? (
                <p className="text-xs text-gray-600">{described.detail}</p>
              ) : null}
              {/*
                G5-WIRE-1: this copy used to make TWO claims in one sentence and
                only one of them has changed. "Gate rules come from the project
                template" is still true — criteria are written once at
                instantiation and there is no create/update route for a gate
                definition, so they are frozen for the life of the project.
                What is no longer true is that NOTHING here is configurable:
                approvers now are. The two claims are kept apart deliberately so
                the panel never implies criteria can be edited.
              */}
              <p className="text-xs text-gray-500">
                Gate rules come from the project template.
              </p>
              <p className="text-xs text-gray-500">
                {status === 'APPROVED'
                  ? 'Criteria cannot be edited here.'
                  : 'Criteria cannot be edited here. Approvers can be changed below.'}
              </p>
              {(gate?.requiredChecklist?.items ?? []).length > 0 ? (
                <ul
                  className="list-disc space-y-1 pl-5 text-sm text-gray-800"
                  data-testid="phase-gate-criteria"
                >
                  {(gate?.requiredChecklist?.items ?? []).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          )}
        </section>

        {/*
          G5-WIRE-1 — approver configuration.

          Mounted here rather than in the admin console: administration is a
          read-only registry, and the authoring route is workspace-write.

          ⚠️ The lock is derived UP FRONT from submissionStatus, not discovered
          on save. GATE_CHAIN_LOCKED is a write failure, so before this the only
          way to learn a chain was frozen was to fill the form in and be refused.
          DRAFT and SUBMITTED are exactly the two statuses assertNoOpenSubmission
          keys on, and the panel already holds submissionStatus — no request
          needed to know.

          ⚠️ No role gate. :347 records that `isAdmin` was removed as the wrong
          axis, and the UI is never an authorization boundary. The affordance is
          shown and the server's 403 answers, which the editor renders as the
          membership problem it is.
        */}
        {definitionExists && gate?.gateDefinitionId ? (
          <section data-testid="phase-gate-approvers">
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-700">
              Approvers
            </h3>
            {approversLocked ? (
              <div
                data-testid="phase-gate-approvers-locked"
                className="space-y-1 rounded border border-gray-200 bg-gray-50 p-3 text-sm"
                role="status"
              >
                <p className="text-gray-900">
                  Approvers are locked while an approval attempt is in progress (
                  {status}).
                </p>
                <p className="text-xs text-gray-600">
                  Decide or withdraw that attempt before changing who approves
                  this gate.
                </p>
                <p className="text-xs text-gray-600">
                  Reordering the existing steps is still possible while an
                  attempt is in flight; replacing or removing the chain is not.
                </p>
              </div>
            ) : !chainReadReady ? (
              <p className="text-sm text-gray-500" data-testid="phase-gate-approvers-loading">
                Loading approvers…
              </p>
            ) : (
              <>
                <ApprovalChainEditor
                  gateDefinitionId={gate.gateDefinitionId}
                  gateName={phaseName}
                  hideWriteActions={gate?.submissionStatus === 'APPROVED'}
                  suppressSavedAck
                  initialSteps={
                    chainSteps && chainSteps.length > 0 ? chainSteps : undefined
                  }
                  onSaved={(chain) => {
                    setApproversSaved(true);
                    setChainSteps(chain.steps);
                    onChanged?.();
                  }}
                />
                {approversSaved ? (
                  <p
                    className="mt-3 text-sm text-green-700"
                    role="status"
                    data-testid="phase-gate-approvers-saved"
                  >
                    Approvers saved.
                  </p>
                ) : null}
              </>
            )}
          </section>
        ) : null}

        {definitionExists && evaluation?.items?.length ? (
          <section data-testid="phase-gate-evaluation">
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-700">
              Checks
            </h3>
            <ul className="space-y-1 text-xs text-gray-700">
              {evaluation.items.map((item, i) => (
                <li
                  key={`${item.code ?? 'check'}-${i}`}
                  className={item.blocking ? 'text-red-700' : 'text-gray-600'}
                >
                  {item.message || item.code || 'Check'}
                  {item.blocking ? ' (blocking)' : ''}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {/* Chain progress (read-only from approval-state).
            PHASEGATEPANEL-REJECTION-1: denial ≠ empty — never collapse. */}
        {approvalStateRead === 'denied' ? (
          <section
            data-testid="phase-gate-approval-state-denied"
            className="rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800"
            role="status"
          >
            <p>You may not view this.</p>
          </section>
        ) : approvalStateRead === 'empty' ? (
          <section
            data-testid="phase-gate-approval-state-empty"
            className="text-xs text-gray-500"
            role="status"
          >
            <p>No approval progress yet.</p>
          </section>
        ) : chainExecutionState ? (
          <section data-testid="phase-gate-chain-progress">
            <div className="mb-2 flex items-center gap-2">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
                Approval progress
              </h3>
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                  chainExecutionState.chainStatus === 'COMPLETED'
                    ? 'bg-green-100 text-green-800'
                    : chainExecutionState.chainStatus === 'REJECTED'
                      ? 'bg-red-100 text-red-800'
                      : chainExecutionState.chainStatus === 'IN_PROGRESS'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-600'
                }`}
              >
                {chainExecutionState.chainStatus}
              </span>
            </div>
            <ol className="space-y-1.5">
              {chainExecutionState.steps.map((step: StepApprovalState) => (
                <li key={step.stepId} className="flex items-center gap-2 text-xs">
                  <span
                    className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-medium ${
                      step.status === 'APPROVED'
                        ? 'bg-green-200 text-green-800'
                        : step.status === 'REJECTED'
                          ? 'bg-red-200 text-red-800'
                          : step.status === 'ACTIVE'
                            ? 'bg-blue-200 text-blue-800'
                            : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {step.status === 'APPROVED'
                      ? '✓'
                      : step.status === 'REJECTED'
                        ? '✕'
                        : step.stepOrder}
                  </span>
                  <span
                    className={
                      step.status === 'ACTIVE' ? 'font-medium text-gray-900' : 'text-gray-600'
                    }
                  >
                    {approvalStepLabel({
                      requiredUserId: chainSteps?.find(
                        (authored) => authored.stepOrder === step.stepOrder,
                      )?.requiredUserId,
                      requiredRole: chainSteps?.find(
                        (authored) => authored.stepOrder === step.stepOrder,
                      )?.requiredRole,
                      storedName:
                        chainSteps?.find(
                          (authored) => authored.stepOrder === step.stepOrder,
                        )?.name ?? step.name,
                    })}
                  </span>
                  {awaitingApproversText(step) ? (
                    <span
                      className="text-gray-500"
                      data-testid={`phase-gate-step-awaiting-${step.stepId}`}
                    >
                      {awaitingApproversText(step)}
                    </span>
                  ) : null}
                  {/* SOD-RENDER-1: server-provided flag only, never derived
                      from comparing decider/submitter ids client-side. */}
                  <SelfApprovedBadge
                    selfApproved={step.decisions.some((d) => d.selfApproved === true)}
                    testIdSuffix={step.stepId}
                  />
                </li>
              ))}
            </ol>
          </section>
        ) : null}

        {/* Open / draft / evidence / submit */}
        {showOpenCta || showReopen ? (
          <section>
            {status === 'MORE_INFORMATION' && infoQuestionFromState ? (
              <div
                className="mb-3 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950"
                data-testid="phase-gate-more-information-question"
                role="status"
              >
                <p className="font-medium">Request changes</p>
                <p className="mt-1">Question: {infoQuestionFromState}</p>
              </div>
            ) : null}
            {status === 'MORE_INFORMATION' ? (
              <label className="mb-2 block text-sm" htmlFor="phase-gate-resubmit-response">
                Response
                <textarea
                  id="phase-gate-resubmit-response"
                  data-testid="phase-gate-resubmit-response"
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
                  rows={3}
                  value={infoResponse}
                  onChange={(e) => setInfoResponse(e.target.value)}
                />
              </label>
            ) : null}
            <div className="flex items-center gap-3">
              {status === 'MORE_INFORMATION' && infoResponse.trim() === '' ? (
                <p className="text-sm text-gray-600" data-testid="phase-gate-response-required">
                  A response is required
                </p>
              ) : null}
              <button
                type="button"
                data-testid="phase-gate-open-submission"
                onClick={() => void (showReopen ? handleResubmit() : handleOpenSubmission())}
                disabled={busy || (status === 'MORE_INFORMATION' && infoResponse.trim() === '')}
                className="flex items-center gap-1 rounded bg-indigo-600 px-3 py-1.5 text-sm text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                <Plus className="h-4 w-4" />
                {showReopen ? 'Resubmit' : 'Open submission'}
              </button>
            </div>
          </section>
        ) : null}

        {showDraftActions && submissionId ? (
          <section className="space-y-3 rounded-lg border border-indigo-200 bg-indigo-50 p-3">
            <h3 className="text-sm font-semibold text-indigo-800">Draft submission</h3>

            <div>
              <div className="mb-1 flex items-center gap-1 text-xs font-medium text-gray-600">
                <Paperclip className="h-3.5 w-3.5" /> Evidence
              </div>
              {evidenceFailed ? (
                <UnavailablePanel
                  onRetry={() => void (submissionId && refreshSubmissionSurface(submissionId))}
                  testId="phase-gate-evidence-unavailable"
                />
              ) : evidence.length > 0 ? (
                <ul className="mb-2 space-y-1">
                  {evidence.map((ev) => (
                    <li
                      key={ev.id}
                      className="flex items-center justify-between text-xs text-gray-700"
                      data-testid={`phase-gate-evidence-${ev.id}`}
                    >
                      <span className="min-w-0 flex-1 truncate">
                        {ev.itemName || 'Evidence item'}
                        {ev.artifactName ? (
                          <span className="text-gray-400">
                            {' · '}
                            {ev.artifactName}
                          </span>
                        ) : null}
                        {ev.itemDeleted ? (
                          <span className="text-gray-400"> (item deleted)</span>
                        ) : null}
                        <EvidenceDetails content={ev.content} />
                      </span>
                      <button
                        type="button"
                        className="text-red-600 hover:underline"
                        onClick={() => void handleRemoveEvidence(ev.id)}
                        disabled={busy}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mb-2 text-xs text-gray-500">No evidence attached yet.</p>
              )}

              {attachDenied ? (
                <p className="mb-2 text-xs text-gray-600" data-testid="phase-gate-attach-denied">
                  Only the project manager, a workspace owner or an admin can attach evidence.
                </p>
              ) : attachablesFailed ? (
                <UnavailablePanel
                  onRetry={() => void loadAttachables(submissionId)}
                  testId="phase-gate-attachables-unavailable"
                />
              ) : attachables.length > 0 ? (
                <>
                  <p className="mb-1 text-xs text-gray-500" data-testid="phase-gate-picker-total">
                    {attachablesTotal === 1
                      ? '1 item in this project'
                      : `${attachablesTotal} items in this project`}
                    {pickerPages > 1
                      ? ` · showing ${pickerFrom}-${pickerTo}`
                      : ''}
                  </p>
                  <ul className="mb-2 space-y-1" data-testid="phase-gate-picker-list">
                    {attachables.map((item) => (
                      <li key={item.id} className="flex items-start gap-2 text-sm">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={selectedItemIds.has(item.id) || item.alreadyAttached}
                          disabled={item.alreadyAttached}
                          aria-label={`Select ${item.name}`}
                          onChange={(e) => {
                            const next = new Set(selectedItemIds);
                            if (e.target.checked) next.add(item.id);
                            else next.delete(item.id);
                            setSelectedItemIds(next);
                          }}
                        />
                        <span className="min-w-0 flex-1 truncate">
                          {item.name}
                          <span className="text-xs text-gray-400"> · {item.artifactName}</span>
                          {item.alreadyAttached ? (
                            <span className="text-xs text-gray-400"> · attached</span>
                          ) : null}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {pickerPages > 1 ? (
                    <div className="mb-2 flex items-center gap-2 text-xs">
                      <button
                        type="button"
                        className="rounded border border-gray-300 bg-white px-2 py-0.5 disabled:opacity-50"
                        disabled={pickerPage === 0}
                        onClick={() => void loadAttachables(submissionId, pickerPage - 1)}
                        data-testid="phase-gate-picker-prev"
                      >
                        Previous
                      </button>
                      <span>
                        Page {pickerPage + 1} of {pickerPages}
                      </span>
                      <button
                        type="button"
                        className="rounded border border-gray-300 bg-white px-2 py-0.5 disabled:opacity-50"
                        disabled={pickerPage >= pickerPages - 1}
                        onClick={() => void loadAttachables(submissionId, pickerPage + 1)}
                        data-testid="phase-gate-picker-next"
                      >
                        Next
                      </button>
                    </div>
                  ) : null}
                  <button
                    type="button"
                    data-testid="phase-gate-attach-evidence"
                    onClick={() => void handleAttachEvidence()}
                    disabled={busy || selectedItemIds.size === 0}
                    className="mb-2 rounded bg-white px-3 py-1 text-xs font-medium text-indigo-700 ring-1 ring-indigo-200 hover:bg-indigo-100 disabled:opacity-50"
                  >
                    Attach selected
                  </button>
                </>
              ) : (
                <p className="mb-2 text-xs text-gray-500" data-testid="phase-gate-picker-empty">
                  No evidence items in this project yet. Use Add evidence below.
                </p>
              )}

              {attachDenied ? null : addOpen ? (
                <form
                  className="space-y-2 rounded border border-indigo-200 bg-white p-2"
                  data-testid="phase-gate-add-evidence-form"
                  noValidate
                  onSubmit={(e) => {
                    e.preventDefault();
                    void handleAddEvidence();
                  }}
                >
                  <label className="block text-xs font-medium text-gray-700">
                    Name
                    <input
                      type="text"
                      value={addName}
                      maxLength={EVIDENCE_NAME_MAX}
                      onChange={(e) => setAddName(e.target.value)}
                      className="mt-0.5 w-full rounded border border-gray-300 px-2 py-1 text-sm"
                      data-testid="phase-gate-add-evidence-name"
                    />
                  </label>
                  <label className="block text-xs font-medium text-gray-700">
                    Link
                    <input
                      type="url"
                      value={addUrl}
                      maxLength={EVIDENCE_URL_MAX}
                      placeholder="https://"
                      onChange={(e) => setAddUrl(e.target.value)}
                      className="mt-0.5 w-full rounded border border-gray-300 px-2 py-1 text-sm"
                      data-testid="phase-gate-add-evidence-url"
                    />
                  </label>
                  <label className="block text-xs font-medium text-gray-700">
                    Note (optional)
                    <textarea
                      value={addNote}
                      maxLength={EVIDENCE_NOTE_MAX}
                      rows={2}
                      onChange={(e) => setAddNote(e.target.value)}
                      className="mt-0.5 w-full rounded border border-gray-300 px-2 py-1 text-sm"
                      data-testid="phase-gate-add-evidence-note"
                    />
                  </label>
                  {addError ? (
                    <p
                      className="rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-800"
                      role="alert"
                      data-testid="phase-gate-add-evidence-error"
                    >
                      {addError}
                    </p>
                  ) : null}
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={busy}
                      className="rounded bg-indigo-600 px-3 py-1 text-xs text-white hover:bg-indigo-700 disabled:opacity-50"
                      data-testid="phase-gate-add-evidence-submit"
                    >
                      Add and attach
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setAddOpen(false);
                        setAddError(null);
                      }}
                      className="rounded border border-gray-300 bg-white px-3 py-1 text-xs disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <button
                  type="button"
                  onClick={() => setAddOpen(true)}
                  className="rounded bg-white px-3 py-1 text-xs font-medium text-indigo-700 ring-1 ring-indigo-200 hover:bg-indigo-100"
                  data-testid="phase-gate-add-evidence"
                >
                  Add evidence
                </button>
              )}
            </div>

            {blockMessage ? (
              <p
                className="rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-800"
                data-testid="phase-gate-submit-blocked"
                role="alert"
              >
                {blockMessage}
              </p>
            ) : null}

            {typeof gate?.unresolvedTaskCount === 'number' && gate.unresolvedTaskCount > 0 ? (
              <div className="text-sm text-amber-900" data-testid="phase-gate-unresolved">
                <p>
                  {gate.unresolvedTaskCount === 1
                    ? '1 task is still open in this phase'
                    : `${gate.unresolvedTaskCount} tasks still open in this phase`}
                </p>
                <button
                  type="button"
                  className="mt-1 text-xs underline"
                  data-testid="phase-gate-unresolved-toggle"
                  onClick={() => setShowOpenTasks((v) => !v)}
                >
                  {showOpenTasks ? 'Hide tasks' : 'See which'}
                </button>
                {showOpenTasks ? (
                  <ul className="mt-1 list-disc pl-4 text-xs" data-testid="phase-gate-unresolved-list">
                    {phaseTasks
                      .filter((task) => task.status !== 'DONE' && !task.closureKind)
                      .map((task) => (
                        <li key={task.id}>{task.title}</li>
                      ))}
                  </ul>
                ) : null}
              </div>
            ) : null}

            {readiness ? <GateReadinessSection readiness={readiness} /> : null}

            <button
              type="button"
              data-testid="phase-gate-submit"
              onClick={() => void handleSubmit()}
              disabled={
                busy ||
                (typeof gate?.unresolvedTaskCount === 'number' && gate.unresolvedTaskCount > 0) ||
                readinessBlocksSubmit(readiness)
              }
              className="flex items-center gap-1 rounded bg-indigo-600 px-3 py-1.5 text-sm text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {busy ? 'Submitting...' : 'Submit for approval'}
            </button>
            {canWithdrawDraft && status === 'DRAFT' ? (
              <div className="mt-2 border-t border-indigo-200 pt-2">
                <button
                  type="button"
                  data-testid="phase-gate-withdraw-draft"
                  onClick={() => void handleWithdraw()}
                  disabled={busy}
                  className="rounded border border-gray-400 bg-white px-3 py-1.5 text-sm text-gray-900 hover:bg-gray-100 disabled:opacity-50"
                >
                  Withdraw draft
                </button>
                {withdrawError ? (
                  <p
                    className="mt-2 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-800"
                    data-testid="phase-gate-withdraw-error"
                    role="alert"
                  >
                    {withdrawError}
                  </p>
                ) : null}
              </div>
            ) : null}
          </section>
        ) : null}

        {/* Approve / reject — submission-ID routes only. */}
        {showApprove ? (
          <section
            className="space-y-2 rounded-lg border border-gray-200 p-3"
            data-testid="phase-gate-decision"
          >
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
              Decision
            </h3>
            <input
              type="text"
              value={decisionNote}
              onChange={(e) => setDecisionNote(e.target.value)}
              placeholder="Reason for rejection (required)"
              aria-label="Reason for rejection"
              className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
              disabled={approveDisabled && callerApproval.fieldsPresent}
            />
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void handleApprove()}
                disabled={busy || approveDisabled}
                aria-disabled={approveDisabled}
                title={approveDisabledReason ?? undefined}
                data-testid={`gate-approve-btn-${submissionId}`}
                className="flex items-center gap-1 rounded bg-green-600 px-3 py-1 text-xs text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-neutral-300 disabled:text-neutral-600"
              >
                <CheckCircle2 className="h-3 w-3" /> Approve
              </button>
              <button
                type="button"
                onClick={() => void handleReject()}
                disabled={busy || decisionNote.trim().length === 0}
                title={
                  decisionNote.trim().length === 0
                    ? 'A note is required to reject.'
                    : undefined
                }
                data-testid={`gate-reject-btn-${submissionId}`}
                className="flex items-center gap-1 rounded bg-red-600 px-3 py-1 text-xs text-white hover:bg-red-700 disabled:opacity-50"
              >
                <XCircle className="h-3 w-3" /> Reject
              </button>
            </div>
            <div
              className="space-y-2 rounded border border-gray-200 bg-gray-50 p-2"
              data-testid="phase-gate-ask-information"
            >
              <label
                htmlFor={`gate-info-question-${submissionId}`}
                className="block text-xs font-medium text-gray-700"
              >
                Question (required)
              </label>
              <textarea
                id={`gate-info-question-${submissionId}`}
                value={infoQuestion}
                onChange={(e) => {
                  setInfoQuestion(e.target.value);
                  setInfoQuestionError(null);
                }}
                rows={2}
                placeholder="What do you need clarified?"
                className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                data-testid={`gate-info-question-input-${submissionId}`}
              />
              {infoQuestionError ? (
                <p
                  className="text-xs text-red-700"
                  role="alert"
                  data-testid={`gate-info-question-error-${submissionId}`}
                >
                  {infoQuestionError}
                </p>
              ) : null}
              <button
                type="button"
                onClick={() => void handleAskInformation()}
                disabled={busy || infoQuestionBlank}
                data-testid={`gate-ask-information-btn-${submissionId}`}
                className="flex items-center gap-1 rounded border border-gray-400 bg-white px-3 py-1 text-xs text-gray-900 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <HelpCircle className="h-3 w-3" /> Request changes
              </button>
            </div>
            {approveDisabled && approveDisabledReason ? (
              <p
                className="text-xs text-neutral-700"
                data-testid={`gate-approve-disabled-reason-${submissionId}`}
                role="status"
              >
                {approveDisabledReason}
              </p>
            ) : null}
          </section>
        ) : null}
      </div>
    </div>
  );
}
